// @ts-check
// =============================================================================
// js/ui/art-prefs.js — which artwork a card shows in the collection / builder.
//
// THE OLD BEHAVIOR (the good one): a card with no chosen art picked a RANDOM
// one the first time it was shown and then kept it — it never snapped back to
// "art #1". This restores that, and persists the choice across sessions:
//
//   1. a valid current card.selectedArt wins (nothing to do),
//   2. else a remembered preference (localStorage), if the player still owns it,
//   3. else a fresh RANDOM pick from collectedArts — remembered so it's stable.
//
// The art switcher / picker call rememberArt() so an explicit choice sticks too.
// Stored per card id in one localStorage blob ("artPrefs").
// =============================================================================

const KEY = 'artPrefs';

/** @type {Record<string, string>|null} */
let cache = null;

/** @returns {Record<string, string>} */
function prefs() {
	if (!cache) {
		try {
			cache = JSON.parse(localStorage.getItem(KEY) || '{}') || {};
		} catch {
			cache = {};
		}
	}
	return cache;
}

/**
 * Persist a card's chosen art (from the switcher/picker, or the first random).
 * @param {number|string} cardId
 * @param {string} art
 * @returns {void}
 */
export function rememberArt(cardId, art) {
	if (art == null) return;
	const map = prefs();
	map[cardId] = art;
	try {
		localStorage.setItem(KEY, JSON.stringify(map));
	} catch {
		// storage full / unavailable — the in-memory pick still holds this session
	}
}

/**
 * Resolve which art a card should DISPLAY, mutating card.selectedArt and
 * remembering a first pick. Returns the art file (or undefined if the card owns
 * none).
 * @param {any} card  A card with collectedArts + selectedArt.
 * @returns {string|undefined}
 */
export function resolveDisplayArt(card) {
	const arts = card.collectedArts || [];
	if (!arts.length) return undefined;

	// 1. A valid current selection stays put (never revert to #1).
	if (card.selectedArt && arts.includes(card.selectedArt)) return card.selectedArt;

	// 2. A remembered preference, if the player still owns that art.
	const pref = prefs()[card.id];
	if (pref && arts.includes(pref)) {
		card.selectedArt = pref;
		return pref;
	}

	// 3. First time seen: pick at random, then remember it so it's stable.
	card.selectedArt = arts[Math.floor(Math.random() * arts.length)];
	rememberArt(card.id, card.selectedArt);
	return card.selectedArt;
}
